import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Chip,
    Paper,
    Grid,
    Link,
    Divider,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import { Language, Link as LinkIcon, Translate, ExpandMore } from '@mui/icons-material';
import { userRequest } from '../../requestMethods';

const AlgorithmicProblemView = ({ problem }) => {
    const navigate = useNavigate();
    const [solutions, setSolutions] = useState([]);
    const [loadingSolutions, setLoadingSolutions] = useState(false);

    const fetchSolutions = useCallback(async () => {
        if (!problem?._id) return;
        
        setLoadingSolutions(true);
        try {
            // Fetch problem with populated solutions through AlgorithmicProblem API
            const response = await userRequest.get(`/algorithmic-problem/${problem._id}`);
            const problemData = response.data;
            
            if (problemData.solutions && problemData.solutions.length > 0) {
                setSolutions(problemData.solutions.map((sol) => ({
                    id: sol._id || sol.id,
                    text: sol.text || '',
                    language: sol.language
                })));
            } else {
                setSolutions([]);
            }
        } catch (error) {
            console.error('Error fetching solutions:', error);
            setSolutions([]);
        } finally {
            setLoadingSolutions(false);
        }
    }, [problem?._id]);

    useEffect(() => {
        // If solutions are already populated (have text property), use them directly
        if (problem?.solutions && problem.solutions.length > 0) {
            const firstSolution = problem.solutions[0];
            if (firstSolution && typeof firstSolution === 'object' && firstSolution.text !== undefined) {
                // Solutions are already populated
                setSolutions(problem.solutions.map((sol) => ({
                    id: sol._id || sol.id,
                    text: sol.text || '',
                    language: sol.language
                })));
            } else {
                // Solutions are just IDs, need to fetch them through AlgorithmicProblem API
                fetchSolutions();
            }
        } else {
            setSolutions([]);
        }
    }, [problem, fetchSolutions]);

    const handleTranslateTask = () => {
        if (problem?.task?._id) {
            navigate(`/text/translate/${problem.task._id}`);
        }
    };

    const handleTranslateSolution = (solutionId) => {
        if (solutionId) {
            navigate(`/text/translate/${solutionId}`);
        }
    };

    const getDifficultyColor = (difficulty) => {
        switch (difficulty) {
            case 'easy':
                return 'success';
            case 'medium':
                return 'warning';
            case 'hard':
                return 'error';
            default:
                return 'default';
        }
    };

    if (!problem) {
        return (
            <Box p={3}>
                <Typography variant="body1" color="text.secondary">
                    No problem selected
                </Typography>
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Grid container spacing={3}>
                {/* Header Section */}
                <Grid item xs={12}>
                    <Typography 
                        variant="h4" 
                        gutterBottom 
                        component="div"
                    >
                        {problem.title}
                    </Typography>
                    
                    <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
                        <Box display="flex" alignItems="center" gap={1}>
                            <Language fontSize="small" color="action" />
                            <Typography variant="body1" color="text.secondary">
                                {problem.language?.name || 'Unknown Language'}
                            </Typography>
                        </Box>
                        
                        {problem.level?.name && (
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                    Level: {problem.level.name}
                                </Typography>
                            </Box>
                        )}

                        {problem.difficulty && (
                            <Chip 
                                label={problem.difficulty.toUpperCase()} 
                                size="small" 
                                color={getDifficultyColor(problem.difficulty)}
                                variant="outlined"
                            />
                        )}
                    </Box>

                    {/* Tags */}
                    <Box mb={3}>
                        <Typography variant="subtitle2" gutterBottom>
                            Tags:
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {problem.tags && problem.tags.map((tag, index) => (
                                <Chip
                                    key={index}
                                    label={tag}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* Problem URL */}
                    {problem.problemUrl && (
                        <Box mb={3} display="flex" alignItems="center" gap={1}>
                            <LinkIcon fontSize="small" color="action" />
                            <Link
                                href={problem.problemUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="body1"
                            >
                                {problem.problemUrl}
                            </Link>
                        </Box>
                    )}

                    {/* Action Buttons */}
                    {problem.task?._id && (
                        <Box mb={3} display="flex" gap={2} flexWrap="wrap">
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Translate />}
                                onClick={handleTranslateTask}
                                size="large"
                            >
                                Translate Task Description
                            </Button>
                        </Box>
                    )}
                </Grid>

                {/* Task Content */}
                <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        Task Description
                    </Typography>
                    
                    <Paper elevation={1} sx={{ p: 3, backgroundColor: '#fafafa' }}>
                        {problem.task?.text ? (
                            <Box 
                                sx={{ 
                                    fontSize: '1rem',
                                    lineHeight: 1.8, 
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: 'inherit'
                                }}
                                dangerouslySetInnerHTML={{ __html: problem.task.text }}
                            />
                        ) : (
                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                No task description available
                            </Typography>
                        )}
                    </Paper>
                </Grid>

                {/* Solutions */}
                <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">
                            Solutions ({solutions.length})
                        </Typography>
                    </Box>

                    {loadingSolutions ? (
                        <Typography variant="body2" color="text.secondary">
                            Loading solutions...
                        </Typography>
                    ) : solutions.length > 0 ? (
                        solutions.map((solution, index) => (
                            <Accordion key={solution.id} sx={{ mb: 1 }}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: '100%', mr: 2 }}>
                                        <Typography variant="subtitle1">
                                            Solution {index + 1}
                                        </Typography>
                                        <Button
                                            size="small"
                                            startIcon={<Translate />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleTranslateSolution(solution.id);
                                            }}
                                            color="primary"
                                        >
                                            Translate
                                        </Button>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                                        {solution.text ? (
                                            <Box 
                                                sx={{ 
                                                    fontSize: '0.95rem',
                                                    lineHeight: 1.7, 
                                                    whiteSpace: 'pre-wrap',
                                                    fontFamily: 'inherit'
                                                }}
                                                dangerouslySetInnerHTML={{ __html: solution.text }}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                                No content available
                                            </Typography>
                                        )}
                                    </Paper>
                                </AccordionDetails>
                            </Accordion>
                        ))
                    ) : (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            No solutions available
                        </Typography>
                    )}
                </Grid>

                {/* Metadata */}
                <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        Metadata
                    </Typography>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                                Created: {new Date(problem.createdAt).toLocaleDateString()}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                                Last Updated: {new Date(problem.updatedAt).toLocaleDateString()}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                                Status: {problem.isReadyToServe ? (
                                    <Chip label="Ready to Serve" size="small" color="success" />
                                ) : (
                                    <Chip label="Draft" size="small" color="warning" />
                                )}
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AlgorithmicProblemView;

